import { gql, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PatientToDo } from 'kb-shared';
import { IntakeFormNotAvailableModal } from 'screens/IntakeForm/components/IntakeFormNotAvailableModal/IntakeFormNotAvailableModal';
import { IntakeFormRoute } from 'screens/IntakeForm/routes/IntakeFormRoute';
import { AbsoluteCentered } from 'screens/IntakeForm/routes/IntakeFormRoute.styled';
import { pageUrl } from 'utilities/pageUrl';

const PATIENT_FORM_ACCESS = gql`
  query patientFormAccess($formId: Int!) {
    patientTodos {
      id
      todoId
      title: name
      linkUrl
      buttonText
      description
      completed
      order
      headingText
      category
      createdAt
    }
    patientFormCompletion(formId: $formId) {
      id
      completedAt
    }
  }
`;

type PatientFormAccessResult = {
  patientTodos: PatientToDo[];
  patientFormCompletion: {
    id: string;
    completedAt: string | null;
  } | null;
};

const formIdFromLinkUrl = (linkUrl: string | null | undefined): number | null => {
  if (!linkUrl) return null;

  try {
    const parsed = new URL(linkUrl, 'https://portal.kindbody.com');
    const match = parsed.pathname.match(/^\/patient-forms\/(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  } catch {
    return null;
  }
};

export const PatientForm = () => {
  const navigate = useNavigate();
  const { formId: rawFormId } = useParams<{ formId: string }>();
  const [formUnavailableReason, setFormUnavailableReason] = useState<string | null>(null);
  const parsedFormId = Number(rawFormId);
  const isValidFormId = Number.isInteger(parsedFormId) && parsedFormId > 0;

  const { data, loading } = useQuery<PatientFormAccessResult>(PATIENT_FORM_ACCESS, {
    variables: { formId: parsedFormId },
    skip: !isValidFormId,
    onError: () => {
      setFormUnavailableReason('FORM_NOT_AVAILABLE');
    }
  });

  if (loading || (!data && isValidFormId)) return null;

  const hasIncompleteTodo = (data?.patientTodos ?? []).some(
    todo => formIdFromLinkUrl(todo.linkUrl) === parsedFormId
  );
  const hasCompletedForm = Boolean(data?.patientFormCompletion?.completedAt);
  const showForm = hasIncompleteTodo && !formUnavailableReason;
  const unavailableMessage =
    formUnavailableReason === 'FORM_ALREADY_COMPLETED' || hasCompletedForm
      ? 'Your form has already been submitted!'
      : 'This form is currently not available.';

  return (
    <>
      {showForm && (
        <IntakeFormRoute
          formId={parsedFormId}
          onFormBecomeUnavailable={reason =>
            setFormUnavailableReason(reason ?? 'FORM_NOT_AVAILABLE')
          }
        />
      )}
      {!showForm && (
        <AbsoluteCentered>
          <IntakeFormNotAvailableModal
            visible={true}
            message={unavailableMessage}
            onDismiss={() => navigate(pageUrl.dashboard())}
          />
        </AbsoluteCentered>
      )}
    </>
  );
};
